/**
 * Authentication Service
 * Handles all auth-related API calls and token management
 */
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001') + '/api/auth';

/**
 * Register a new user with email and password
 */
export const signup = async (email, password, fullName = null, companyName = null) => {
  const response = await axios.post(`${API_URL}/signup`, {
    email,
    password,
    full_name: fullName,
    company_name: companyName
  });
  return response.data;
};

/**
 * Login with email and password
 * Stores token and user data in localStorage
 */
export const login = async (email, password) => {
  const response = await axios.post(`${API_URL}/login`, {
    email,
    password
  });

  if (response.data.access_token) {
    localStorage.setItem('token', response.data.access_token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
  }

  return response.data;
};

/**
 * Logout - clear tokens and user data
 */
export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

/**
 * Get current user from localStorage
 */
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      console.error('Error parsing user data:', e);
      return null;
    }
  }
  return null;
};

/**
 * Get auth token from localStorage
 */
export const getToken = () => {
  return localStorage.getItem('token');
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * Verify email with token
 */
export const verifyEmail = async (token) => {
  const response = await axios.post(`${API_URL}/verify-email/${token}`);
  return response.data;
};

/**
 * Resend verification email
 */
export const resendVerification = async (email) => {
  const response = await axios.post(`${API_URL}/resend-verification`, {
    email
  });
  return response.data;
};

/**
 * Get current user info from server
 */
export const getMe = async () => {
  const token = getToken();
  const response = await axios.get(`${API_URL}/me`, {
    headers: { Authorization: `Bearer ${token}` }
  });

  // Update stored user data
  if (response.data) {
    localStorage.setItem('user', JSON.stringify(response.data));
  }

  return response.data;
};

/**
 * Get usage statistics (trial status)
 */
export const getUsageStats = async () => {
  const token = getToken();
  const response = await axios.get(`${API_URL}/usage/stats`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

/**
 * Initiate Google OAuth flow
 */
export const loginWithGoogle = () => {
  window.location.href = `${API_URL}/google`;
};

/**
 * Handle Google OAuth callback (called from redirect)
 * Note: Token storage is now handled directly in GoogleCallback.js
 * to avoid race conditions
 */
export const handleGoogleCallback = (token, userId) => {
  if (token) {
    localStorage.setItem('token', token);
  }
};

/**
 * Update stored user data (e.g., after trial status changes)
 */
export const updateStoredUser = (userData) => {
  const currentUser = getCurrentUser();
  if (currentUser) {
    const updatedUser = { ...currentUser, ...userData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }
};

/**
 * Request password reset OTP
 */
export const forgotPassword = async (email) => {
  const response = await axios.post(`${API_URL}/forgot-password`, {
    email
  });
  return response.data;
};

/**
 * Verify password reset OTP
 */
export const verifyResetOTP = async (email, otp) => {
  const response = await axios.post(`${API_URL}/verify-reset-otp`, {
    email,
    otp
  });
  return response.data;
};

/**
 * Reset password with OTP
 */
export const resetPassword = async (email, otp, newPassword) => {
  const response = await axios.post(`${API_URL}/reset-password`, {
    email,
    otp,
    new_password: newPassword
  });
  return response.data;
};

export default {
  signup,
  login,
  logout,
  getCurrentUser,
  getToken,
  isAuthenticated,
  verifyEmail,
  resendVerification,
  getMe,
  getUsageStats,
  loginWithGoogle,
  handleGoogleCallback,
  updateStoredUser,
  forgotPassword,
  verifyResetOTP,
  resetPassword
};
